import { forwardRef, useEffect, useRef, useState } from "react";
import Bugsnag from "@bugsnag/js";
import { Autocomplete } from "@hireart/component-library";
import debounce from "lodash/debounce";
import PropTypes from "prop-types";
import { Toasts } from "common/components/Toasts";

const defaultSearchConfig = {
  types: ["address"],
};

export const getIsErrorStatus = (status) => {
  const errorStatuses = [
    window.google.maps.places.PlacesServiceStatus.UNKNOWN_ERROR,
    window.google.maps.places.PlacesServiceStatus.INVALID_REQUEST,
    window.google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT,
    window.google.maps.places.PlacesServiceStatus.REQUEST_DENIED,
  ];

  return errorStatuses.includes(status);
};

export const GooglePlacesAutocomplete = forwardRef(
  (
    {
      "aria-describedby": ariaDescribedBy = undefined,
      className = "",
      hasError = false,
      id,
      isDisabled = false,
      isLabelUnstyled = false,
      isOptional = false,
      label,
      name = undefined,
      numSkeletons = undefined,
      onChange,
      onClear,
      onSelect = () => {},
      searchConfig = defaultSearchConfig,
      skeletonComponent = undefined,
      value = "",
    },
    ref
  ) => {
    const [addresses, setAddresses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const autocompleteServiceRef = useRef(null);

    useEffect(() => {
      autocompleteServiceRef.current =
        new window.google.maps.places.AutocompleteService();
    }, []);

    const resolveSearchResults = (predictions, status) => {
      const isErrorStatus = getIsErrorStatus(status);
      const hasMatches =
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        predictions?.length;

      if (hasMatches) {
        setAddresses(
          predictions.map((prediction) => ({
            text: prediction.description,
            value: prediction.place_id,
          }))
        );
      } else {
        if (isErrorStatus) {
          Bugsnag.notify(
            new Error(
              `A status error (${status}) was received while loading addresses from Google Places API`
            )
          );

          Toasts.error(
            "An error occurred while loading the addresses. Please try again."
          );
        }

        setAddresses([]);
      }

      setIsLoading(false);
    };

    const search = debounce((query) => {
      return autocompleteServiceRef.current.getPlacePredictions(
        {
          ...searchConfig,
          input: query,
        },
        resolveSearchResults
      );
    }, 500);

    const handleSearch = (e, newValue) => {
      onChange(e, newValue);

      if (newValue.length === 0) return;

      setIsLoading(true);
      search(newValue);
    };

    const handleClear = () => {
      onClear();
      setAddresses([]);
    };

    const handleSelectOption = (e, optionId) => {
      const selectedOption = addresses.find(
        (address) => address.value === optionId
      );

      onSelect(e, selectedOption);
      onChange(e, selectedOption.text);
      setAddresses([selectedOption]);
    };

    return (
      <Autocomplete
        ref={ref}
        aria-describedby={ariaDescribedBy}
        className={className}
        hasError={hasError}
        id={id}
        isDisabled={isDisabled}
        isLabelUnstyled={isLabelUnstyled}
        isLoading={isLoading}
        isOptional={isOptional}
        label={label}
        name={name}
        numSkeletons={numSkeletons}
        onClear={handleClear}
        onChange={handleSearch}
        onSelectOption={handleSelectOption}
        options={addresses}
        skeletonComponent={skeletonComponent}
        value={value}
        size="md"
      />
    );
  }
);

GooglePlacesAutocomplete.displayName = "GooglePlacesAutocomplete";

GooglePlacesAutocomplete.propTypes = {
  "aria-describedby": PropTypes.string,
  className: PropTypes.string,
  hasError: PropTypes.bool,
  isDisabled: PropTypes.bool,
  id: PropTypes.string.isRequired,
  isLabelUnstyled: PropTypes.bool,
  isOptional: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string,
  numSkeletons: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  /* searchConfig can include any option provided by the Google Places API (see Places Autocomplete Service docs) */
  searchConfig: PropTypes.shape({}),
  skeletonComponent: PropTypes.element,
  value: PropTypes.string,
};
